import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, LinearProgress, IconButton,
    Snackbar, Alert, useMediaQuery, useTheme,
    Container, Divider, Accordion, AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    LineChart, Line, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid,
    BarChart, Bar, LabelList,
} from 'recharts';
import CountUp from 'react-countup';
import {
    TrendingUpOutlined, ShieldOutlined, EmojiEventsOutlined,
    SavingsOutlined, InfoOutlined, NorthEastOutlined,
    CalendarTodayOutlined, AutoGraphOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

/* ─── Brand ── (matches WalletPage / ActivityScreen) ─────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    gold:    '#F5C842',
};

/* ─── Data ───────────────────────────────────────────────────────── */
const scoreData = [
    { month: 'May', score: 420 },
    { month: 'Jun', score: 500 },
    { month: 'Jul', score: 560 },
    { month: 'Aug', score: 580 },
    { month: 'Sep', score: 653 },
];

const breakdownData = [
    { name: 'Income Stability',            score: 85, grade: 'Good'      },
    { name: 'Expense Ratio',               score: 75, grade: 'Good'      },
    { name: 'Recurring Payment',           score: 90, grade: 'Excellent' },
    { name: 'Overdraft Behavior',          score: 70, grade: 'Fair'      },
    { name: 'Loan Repayment',              score: 60, grade: 'Average'   },
    { name: 'Account Profile',             score: 80, grade: 'Good'      },
];

const scoreExplanations: Record<string, string> = {
    'Income Stability': `Based on how consistent income is over time, using standard deviation of monthly income from Open Banking data.\n\n• Fetch salary/income credits over 6 months\n• Group by month and total each month\n• Compute standard deviation\n• Divide by average income = income volatility\n\nScore: 100 − (Income Volatility × 100)`,
    'Expense Ratio': `Ratio of expenses to income. Lower is better.\n\n• ≤ 0.5 → Excellent (90)\n• 0.51–0.75 → Good (75)\n• 0.76–1.0 → Average (55)\n• > 1.0 → Poor (35)`,
    'Recurring Payment Reliability': `Consistency of recurring payments like rent or utilities.\n\n• ≥ 95% success → Excellent (90)\n• 85–94% → Good (75)\n• < 85% → Fair (60)`,
    'Overdraft Behavior': `Overdraft occurrences in the last 90 days.\n\n• 0 overdrafts → Excellent (90)\n• 1–2 → Fair (70)\n• 3+ → Risky (50)`,
    'Loan Repayment Ratio': `Monthly loan burden vs. income.\n\n• ≤ 0.2 → Healthy (85)\n• 0.21–0.35 → Moderate (70)\n• 0.36–0.5 → High (55)\n• > 0.5 → Concerning (35)`,
    'Account Profile': `Account age and breadth.\n\n• ≥ 24 months → 90\n• 12–23 months → 75\n• < 12 months → 50\n• +10 if multiple accounts`,
};

const gradeColor = (grade: string) => {
    if (grade === 'Excellent') return B.green;
    if (grade === 'Good')      return '#34C759';
    if (grade === 'Fair')      return '#FF9500';
    return B.red;
};

/* ─── Custom tooltips ───────────────────────────────────────────── */
const LineTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: B.dark, border: `1px solid ${alpha(B.light,.2)}`, borderRadius: 2, px: 1.5, py: 1, boxShadow: `0 8px 24px ${alpha(B.deep,.6)}` }}>
            <Typography sx={{ color: alpha('#fff',.4), fontSize: '.6rem', letterSpacing: '.08em', textTransform: 'uppercase', mb: .25 }}>{payload[0].payload.month}</Typography>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.9rem', fontVariantNumeric: 'tabular-nums' }}>{payload[0].value}</Typography>
        </Box>
    );
};

const BarTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <Box sx={{ background: B.dark, border: `1px solid ${alpha(B.light,.2)}`, borderRadius: 2, px: 1.5, py: 1, boxShadow: `0 8px 24px ${alpha(B.deep,.6)}` }}>
            <Typography sx={{ color: alpha('#fff',.45), fontSize: '.62rem', mb: .25, letterSpacing: '.04em' }}>{d.name}</Typography>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.88rem' }}>{d.score} <span style={{ color: gradeColor(d.grade), fontSize: '.72rem' }}>{d.grade}</span></Typography>
        </Box>
    );
};

/* ─── Main ───────────────────────────────────────────────────────── */
const Dashboard: React.FC = () => {
    const navigate  = useNavigate();
    const theme     = useTheme();
    const isMobile  = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark    = theme.palette.mode === 'dark';

    const [showProgress,  setShowProgress]  = useState(false);
    const [reminderOpen,  setReminderOpen]  = useState(false);
    const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

    useEffect(() => {
        const t1 = setTimeout(() => setShowProgress(true), 400);
        const t2 = setTimeout(() => setReminderOpen(true), 1200);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    const currentScore = 653;
    const scoreMax     = 850;
    const scorePct     = (currentScore / scoreMax) * 100;

    return (
        <Box sx={{ minHeight: '100vh', background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 14, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light,.18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.accent,.1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box>
                        <Typography sx={{ color: alpha('#fff',.35), fontSize: '.63rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: .4 }}>Credit Intelligence</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-.03em', fontFamily: "'Instrument Serif', serif" }}>Your Dashboard</Typography>
                    </Box>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .6, background: alpha(B.green,.15), border: `1px solid ${alpha(B.green,.3)}`, borderRadius: 8, px: 1.25, py: .5 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: B.green, boxShadow: `0 0 5px ${B.green}` }} />
                        <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.06em' }}>LOW RISK</Typography>
                    </Box>
                </Box>

                {/* Big score display */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: alpha('#fff',.35), fontSize: '.63rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: 1 }}>NG Credit Score</Typography>
                    <Box display="flex" alignItems="baseline" justifyContent="center" gap={1.5} mb={.5}>
                        <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: { xs: '4.5rem', sm: '5.5rem' }, fontWeight: 400, lineHeight: 1, letterSpacing: '-.03em' }}>
                            <CountUp end={currentScore} duration={1.2} />
                        </Typography>
                        <Box>
                            <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '.88rem' }}>▲ 60</Typography>
                            <Typography sx={{ color: alpha('#fff',.3), fontSize: '.62rem' }}>/ {scoreMax}</Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ color: alpha('#fff',.35), fontSize: '.7rem', letterSpacing: '.06em', mb: 2.5 }}>
                        Next update in <span style={{ color: alpha('#fff',.6), fontWeight: 600 }}>2 days</span>
                    </Typography>

                    {/* Score arc / progress */}
                    <Box sx={{ maxWidth: 320, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .75 }}>
                            <Typography sx={{ color: alpha('#fff',.3), fontSize: '.62rem' }}>300</Typography>
                            <Typography sx={{ color: alpha('#fff',.3), fontSize: '.62rem' }}>850</Typography>
                        </Box>
                        <Box sx={{ height: 6, borderRadius: 6, background: alpha('#fff',.1), overflow: 'hidden' }}>
                            <Box sx={{ width: `${scorePct}%`, height: '100%', background: `linear-gradient(90deg, ${B.purple}, ${B.green})`, borderRadius: 6, transition: 'width 1.2s cubic-bezier(.16,1,.3,1)' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Box sx={{ background: alpha(B.green,.15), border: `1px solid ${alpha(B.green,.25)}`, borderRadius: 8, px: 1.25, py: .4 }}>
                                <Typography sx={{ color: B.green, fontSize: '.65rem', fontWeight: 700, letterSpacing: '.06em' }}>GOOD STANDING</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ══ FLOATING CONTENT ═══════════════════════════════════ */}
            <Container maxWidth="sm" sx={{ mt: -8, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Score trend chart ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, backdropFilter: 'blur(20px)', p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Score Trend</Typography>
                            <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink,.35), mt: .2 }}>Last 5 months</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={.75}>
                            <NorthEastOutlined sx={{ fontSize: 14, color: B.green }} />
                            <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>+233 pts</Typography>
                        </Box>
                    </Box>
                    <ResponsiveContainer width="100%" height={isMobile ? 140 : 180}>
                        <LineChart data={scoreData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                            <CartesianGrid stroke={alpha(isDark ? '#fff' : '#000',.05)} strokeDasharray="4 4" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: alpha(isDark ? '#fff' : B.ink,.35) as string }} axisLine={false} tickLine={false} />
                            <YAxis domain={[350, 700]} tick={{ fontSize: 10, fill: alpha(isDark ? '#fff' : B.ink,.28) as string }} axisLine={false} tickLine={false} />
                            <Tooltip content={<LineTooltip />} />
                            <defs>
                                <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor={B.purple} />
                                    <stop offset="100%" stopColor={B.green} />
                                </linearGradient>
                            </defs>
                            <Line type="monotone" dataKey="score" stroke="url(#scoreGrad)" strokeWidth={2.5} dot={{ fill: B.green, strokeWidth: 0, r: 3 }} activeDot={{ r: 5, fill: B.green, strokeWidth: 0 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>

                {/* ── Risk level + Goal ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    {/* Risk */}
                    <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: B.greenDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ShieldOutlined sx={{ color: B.green, fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontSize: '.65rem', color: alpha(isDark ? '#fff' : B.ink,.35), letterSpacing: '.1em', textTransform: 'uppercase', mt: .5 }}>Risk Level</Typography>
                        <Typography sx={{ fontFamily: "'Instrument Serif', serif", fontSize: '1.6rem', color: B.green, lineHeight: 1, letterSpacing: '-.02em' }}>Low</Typography>
                        <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink,.35), lineHeight: 1.4 }}>You're a trusted borrower</Typography>
                    </Box>
                    {/* Goal */}
                    <Box onClick={() => navigate('/goals')} sx={{
                        borderRadius: 3, cursor: 'pointer',
                        background: `linear-gradient(145deg, ${B.dark}, ${B.mid})`,
                        boxShadow: `0 8px 28px ${alpha(B.dark,.4)}`,
                        p: 2.5, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden', position: 'relative',
                        transition: 'transform .25s',
                        '&:hover': { transform: 'translateY(-3px)' },
                    }}>
                        <Box sx={{ position: 'absolute', top: -20, right: -20, width: 90, height: 90, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.gold,.18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                        <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(B.gold,.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <EmojiEventsOutlined sx={{ color: B.gold, fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontSize: '.65rem', color: alpha('#fff',.38), letterSpacing: '.1em', textTransform: 'uppercase', mt: .5 }}>Score Goal</Typography>
                        <Typography sx={{ fontFamily: "'Instrument Serif', serif", fontSize: '1.6rem', color: '#fff', lineHeight: 1, letterSpacing: '-.02em' }}>750</Typography>
                        <Typography sx={{ fontSize: '.68rem', color: alpha('#fff',.38), lineHeight: 1.4 }}>Set a target →</Typography>
                    </Box>
                </Box>

                {/* ── Savings / Credit Builder ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, backdropFilter: 'blur(20px)', overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(B.green,.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <SavingsOutlined sx={{ color: B.green, fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Credit Builder</Typography>
                                    <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink,.35) }}>Savings progress</Typography>
                                </Box>
                            </Box>
                            <Typography sx={{ fontFamily: "'Instrument Serif', serif", fontSize: '1.3rem', color: B.green, fontWeight: 400 }}>₦450k</Typography>
                        </Box>

                        <LinearProgress variant="determinate" value={showProgress ? 75 : 0}
                                        sx={{ height: 6, borderRadius: 6, bgcolor: alpha(B.green,.1), mb: 1,
                                            '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${B.purple}, ${B.green})`, borderRadius: 6, transition: 'transform 1.2s cubic-bezier(.16,1,.3,1) !important' } }} />

                        <Box display="flex" justifyContent="space-between">
                            <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink,.35) }}>₦450,000</Typography>
                            <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink,.35) }}>₦600,000 target</Typography>
                        </Box>

                        <Box sx={{ mt: 2, p: 1.75, borderRadius: 2, background: alpha(isDark ? '#fff' : B.dark,.04), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography sx={{ fontSize: '.7rem', color: alpha(isDark ? '#fff' : B.ink,.4), mb: .2 }}>75% toward goal</Typography>
                                <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color: isDark ? '#fff' : B.ink }}>₦150,000 remaining</Typography>
                            </Box>
                            <Box sx={{ background: alpha(B.green,.1), border: `1px solid ${alpha(B.green,.2)}`, borderRadius: 8, px: 1, py: .4 }}>
                                <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.04em' }}>ON TRACK</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* ── Score breakdown bar chart ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Score Breakdown</Typography>
                            <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink,.35), mt: .2 }}>6 factors · hover for detail</Typography>
                        </Box>
                        <AutoGraphOutlined sx={{ fontSize: 16, color: alpha(isDark ? '#fff' : B.ink,.25) }} />
                    </Box>

                    {/* Category bars — custom, not recharts horizontal */}
                    {breakdownData.map((d, i) => (
                        <Box key={i} sx={{ mb: i < breakdownData.length - 1 ? 2 : 0 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={.7}>
                                <Typography sx={{ fontSize: '.75rem', fontWeight: 500, color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em' }}>{d.name}</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography sx={{ fontSize: '.72rem', fontWeight: 700, color: isDark ? '#fff' : B.ink, fontVariantNumeric: 'tabular-nums' }}>{d.score}</Typography>
                                    <Box sx={{ background: alpha(gradeColor(d.grade),.12), borderRadius: 6, px: .9, py: .2 }}>
                                        <Typography sx={{ color: gradeColor(d.grade), fontSize: '.6rem', fontWeight: 700, letterSpacing: '.04em' }}>{d.grade.toUpperCase()}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ height: 5, borderRadius: 4, background: alpha(isDark ? '#fff' : B.dark,.06) }}>
                                <Box sx={{ width: `${d.score}%`, height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${B.purple}, ${gradeColor(d.grade)})`, transition: 'width 1s cubic-bezier(.16,1,.3,1)' }} />
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* ── How scores are calculated ── */}
                <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark,.07)}` }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(B.purple,.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <InfoOutlined sx={{ color: B.purple, fontSize: 17 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>How Scores Work</Typography>
                            <Typography sx={{ fontSize: '.66rem', color: alpha(isDark ? '#fff' : B.ink,.35) }}>Tap a factor to learn more</Typography>
                        </Box>
                    </Box>
                    <Box>
                        {Object.entries(scoreExplanations).map(([name, explanation], i, arr) => (
                            <React.Fragment key={name}>
                                <Box
                                    onClick={() => setExpandedAccordion(expandedAccordion === name ? false : name)}
                                    sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark,.025) } }}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: gradeColor(breakdownData.find(d => d.name.startsWith(name.split(' ')[0]))?.grade || 'Good'), flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '.82rem', fontWeight: 500, color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em' }}>{name}</Typography>
                                    </Box>
                                    <Box sx={{ transition: 'transform .2s', transform: expandedAccordion === name ? 'rotate(90deg)' : 'none' }}>
                                        <ArrowForwardIosIcon sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink,.25) }} />
                                    </Box>
                                </Box>
                                {expandedAccordion === name && (
                                    <Box sx={{ px: 2.5, pb: 2, borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark,.05)}` }}>
                                        <Typography sx={{ fontSize: '.76rem', color: alpha(isDark ? '#fff' : B.ink,.55), lineHeight: 1.8, whiteSpace: 'pre-line', pt: 1.75 }}>
                                            {explanation}
                                        </Typography>
                                    </Box>
                                )}
                                {i < arr.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark,.055) }} />}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

            </Container>

            {/* ── Payment reminder ── */}
            <Snackbar open={reminderOpen} autoHideDuration={5000} onClose={() => setReminderOpen(false)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Box sx={{ background: B.dark, border: `1px solid ${alpha(B.light,.25)}`, borderRadius: 3, px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2, boxShadow: `0 16px 48px ${alpha(B.deep,.6)}`, maxWidth: 380 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha('#FF9500',.15), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <CalendarTodayOutlined sx={{ color: '#FF9500', fontSize: 17 }} />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ color: '#fff', fontSize: '.78rem', fontWeight: 600, letterSpacing: '-.01em' }}>Upcoming auto-payment</Typography>
                        <Typography sx={{ color: alpha('#fff',.45), fontSize: '.7rem', mt: .2 }}>₦10,000 due <span style={{ color: '#FF9500', fontWeight: 600 }}>Sep 5, 2024</span> · Tap to pay early</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setReminderOpen(false)} sx={{ color: alpha('#fff',.35), '&:hover': { color: '#fff' } }}>
                        <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                </Box>
            </Snackbar>
        </Box>
    );
};

export default Dashboard;